import React from 'react';
import type { DailyNoteRecord } from '../api';
import { DailyNoteEditor } from './DailyNoteEditor';
import { addDays, formatDisplayDate } from '../utils/date';

interface Props {
  currentDate: string;
  note: DailyNoteRecord | null;
  recentNotes: DailyNoteRecord[];
  onChangeDate: (date: string) => void;
  onSaveNote: (date: string, updates: { title: string; content: string }) => Promise<void>;
}

export function NotesView({ currentDate, note, recentNotes, onChangeDate, onSaveNote }: Props) {
  return (
    <div className="notes-view" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 16 }}>
      <div style={{ display: 'grid', gap: 16 }}>
        <section
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes workspace</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{formatDisplayDate(currentDate, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onChangeDate(addDays(currentDate, -1))}>Previous</button>
            <button className="btn btn-ghost btn-sm" onClick={() => onChangeDate(addDays(currentDate, 1))}>Next</button>
          </div>
        </section>

        <DailyNoteEditor
          date={currentDate}
          note={note}
          helperText="Keep this lightweight: decisions, meetings, blockers, and what you learned."
          onSave={onSaveNote}
        />
      </div>

      <aside
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 16,
          alignSelf: 'start',
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Recent notes</h2>
        {recentNotes.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No daily notes yet. Capture something from Today or the command bar.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {recentNotes.map((entry) => (
              <button
                key={entry.id}
                className={`task-card${entry.noteDate === currentDate ? ' in_progress' : ''}`}
                style={{ width: '100%', textAlign: 'left', background: 'var(--bg-primary)' }}
                onClick={() => onChangeDate(entry.noteDate)}
              >
                <div className="task-content">
                  <div className="task-title">{entry.title || formatDisplayDate(entry.noteDate, { month: 'short', day: 'numeric' })}</div>
                  <div className="task-meta">
                    <span>{formatDisplayDate(entry.noteDate, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  {entry.content && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                      {truncate(entry.content)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

function truncate(value: string): string {
  return value.length > 120 ? `${value.slice(0, 117)}...` : value;
}
